// src/components/super-admin/StandaloneLicenseContainer.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
    Box,
    Button,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputAdornment,
    Paper,
    Chip,
    Grid,
    Stack,
    Avatar,
    alpha,
    Tooltip,
    CircularProgress,
    Divider,
    Drawer,
    FormLabel,
    FormHelperText,
} from "@mui/material";

// Icons
import {
    SearchOutlined,
    ContentCopy,
    Close,
    Refresh,
    Business,
    Person,
    Email,
    Phone,
    CalendarToday,
    VpnKey,
    Warning,
    CheckCircle,
    Block,
    Delete as DeleteIcon,
    Edit as EditIcon,
    RestartAlt,
    People,
    CheckCircleOutline,
    ErrorOutline,
    Verified,
    Computer,
    Add,
} from "@mui/icons-material";

// Components
import { TableVirtuoso } from "react-virtuoso";

// Utils
import { formatDate } from "../../utils/DateTime";
import { debounce } from "../../utils/Functions";

// Dialogs
import ConfirmationDialog from "../dialog/ConfirmationDialog";

// API
import { HttpStatusCode } from "axios";
import { Severity } from "../../constant/ApplicationVariables";
import useErrorHandler from "../../api/errorHandler/useErrorHandler";

// License API
import {
    getOrganisationList_API,
    getStandaloneLicenseList_API,
    createStandaloneLicense_API,
    updateStandaloneLicense_API,
    revokeStandaloneLicense_API,
    reactivateStandaloneLicense_API,
    deleteStandaloneLicense_API
} from "../../api/controller/LicenseController";

// User API
import { getUserDetail_API } from "../../api/controller/userController";

// Date picker
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useApplicationContext } from "../../context/ApplicationContext";

// ============================================================
// CONSTANTS
// ============================================================

const COLORS = {
    primary: '#14b8a5',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    background: '#F8FAFC',
    card: '#FFFFFF',
};

const STATUS_CONFIG = {
    ACTIVE: { color: 'success', label: 'Active', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    REVOKED: { color: 'error', label: 'Revoked', icon: <Block sx={{ fontSize: 14 }} /> },
    EXPIRED: { color: 'error', label: 'Expired', icon: <ErrorOutline sx={{ fontSize: 14 }} /> },
};

const KPI_CARDS = [
    { id: 'total', label: 'Total', icon: VpnKey, color: COLORS.primary },
    { id: 'active', label: 'Active', icon: CheckCircleOutline, color: COLORS.success },
    { id: 'expired', label: 'Expired', icon: ErrorOutline, color: COLORS.error },
    { id: 'revoked', label: 'Revoked', icon: Block, color: COLORS.warning },
];

// ============================================================
// HELPERS
// ============================================================

const isExpired = (date) => dayjs(date).isBefore(dayjs());

const getStatusConfig = (status, expiryDate) => {
    if (isExpired(expiryDate)) return STATUS_CONFIG.EXPIRED;
    return STATUS_CONFIG[status] || { color: 'default', label: status, icon: null };
};

// ============================================================
// CUSTOM TABLE COMPONENTS FOR VIRTUOSO
// ============================================================

const TableComponents = {
    Scroller: React.forwardRef((props, ref) => (
        <Box
            component="div"
            ref={ref}
            {...props}
            sx={{
                width: '100%',
                overflow: 'auto',
                '& > div': {
                    minWidth: '100%',
                }
            }}
        />
    )),
    Table: React.forwardRef((props, ref) => (
        <table
            ref={ref}
            {...props}
            style={{
                width: '100%',
                minWidth: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                ...props.style,
            }}
        />
    )),
    TableHead: React.forwardRef((props, ref) => (
        <thead ref={ref} {...props} style={{ display: 'table-header-group', ...props.style }} />
    )),
    TableRow: React.forwardRef((props, ref) => (
        <tr ref={ref} {...props} />
    )),
    TableBody: React.forwardRef((props, ref) => (
        <tbody ref={ref} {...props} />
    )),
};

// ============================================================
// COMPONENTS
// ============================================================

// ----- KPI CARDS -----
const KPICards = React.memo(({ stats }) => {
    const theme = useTheme();

    return (
        <Grid container spacing={2}>
            {KPI_CARDS.map((card) => {
                const value = stats[card.id] || 0;
                const Icon = card.icon;

                return (
                    <Grid item xs={12} sm={6} md={3} key={card.id}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2.5,
                                borderRadius: 3,
                                border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                                bgcolor: 'background.paper',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                cursor: 'default',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: theme.shadows[4],
                                    borderColor: alpha(card.color, 0.2),
                                },
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        bgcolor: alpha(card.color, 0.08),
                                        color: card.color,
                                    }}
                                >
                                    <Icon sx={{ fontSize: 22 }} />
                                </Avatar>
                                <Box flex={1}>
                                    <Typography
                                        variant="caption"
                                        color="textSecondary"
                                        sx={{ fontWeight: 500, letterSpacing: 0.3, textTransform: 'uppercase' }}
                                    >
                                        {card.label}
                                    </Typography>
                                    <Typography variant="h4" fontWeight={700} lineHeight={1.2}>
                                        {value}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>
                );
            })}
        </Grid>
    );
});

// ----- LICENSE KEY MODAL -----
const LicenseKeyModal = React.memo(({ open, licenseKey, onClose }) => {
    const theme = useTheme();
    const { handleSnackbar } = useApplicationContext();
    const [copied, setCopied] = useState(false);

    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(licenseKey);
            setCopied(true);
            handleSnackbar("License Key copied!", Severity.success);
            setTimeout(() => setCopied(false), 3000);
        } catch {
            handleSnackbar("Failed to copy", Severity.error);
        }
    }, [licenseKey, handleSnackbar]);

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'rgba(0,0,0,0.5)',
                backdropFilter: 'blur(8px)',
                zIndex: 9999,
                display: open ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Paper
                elevation={24}
                sx={{
                    maxWidth: 520,
                    width: '90%',
                    p: 4,
                    borderRadius: 4,
                    position: 'relative',
                }}
            >
                <IconButton
                    sx={{ position: 'absolute', top: 12, right: 12 }}
                    onClick={onClose}
                >
                    <Close />
                </IconButton>

                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Avatar
                        sx={{
                            width: 48,
                            height: 48,
                            bgcolor: alpha(COLORS.success, 0.1),
                            color: COLORS.success,
                        }}
                    >
                        <Verified sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight={700}>
                            License Key Generated
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Copy and send to customer
                        </Typography>
                    </Box>
                </Stack>

                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                        mb: 3,
                    }}
                >
                    <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                        License Key
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={licenseKey}
                        InputProps={{
                            readOnly: true,
                            sx: {
                                fontFamily: 'monospace',
                                fontSize: '0.85rem',
                                bgcolor: 'transparent',
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
                            },
                        }}
                    />
                </Paper>

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                        onClick={handleCopy}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: COLORS.success,
                            '&:hover': { bgcolor: '#059669' },
                        }}
                    >
                        {copied ? 'Copied!' : 'Copy Key'}
                    </Button>
                </Stack>

                <Box
                    sx={{
                        mt: 3,
                        p: 2,
                        bgcolor: alpha(COLORS.warning, 0.06),
                        borderRadius: 2,
                    }}
                >
                    <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Warning sx={{ fontSize: 14 }} />
                        Copy now — not stored
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
});

// ============================================================
// LICENSE DRAWER
// ============================================================

const LicenseDrawer = React.memo(({ 
    open, 
    onClose, 
    mode, 
    data, 
    onSubmit,
    selectedOrganisation,
    isLoading,
    issuerEmail 
}) => {
    const theme = useTheme();
    const { handleSnackbar } = useApplicationContext();
    const [formData, setFormData] = useState({
        infoKey: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        licenseType: 'Individual',
        expiryDate: null,
        userCount: 1,
    });
    const [errors, setErrors] = useState({});

    // Reset form when drawer opens or data changes
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && data) {
                setFormData({
                    infoKey: data.infoKey || '',
                    customerName: data.customerName || '',
                    customerEmail: data.customerEmail || '',
                    customerPhone: data.customerPhone || '',
                    licenseType: data.licenseType || 'Individual',
                    expiryDate: data.expiryDate ? dayjs(data.expiryDate) : null,
                    userCount: data.userCount || 1,
                });
            } else {
                setFormData({
                    infoKey: '',
                    customerName: '',
                    customerEmail: '',
                    customerPhone: '',
                    licenseType: 'Individual',
                    expiryDate: null,
                    userCount: 1,
                });
            }
            setErrors({});
        }
    }, [open, mode, data]);

    const handleChange = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    }, [errors]);

    const validateForm = useCallback(() => {
        const newErrors = {};
        
        if (mode === 'create' && !formData.infoKey?.trim()) {
            newErrors.infoKey = 'Info Key is required';
        }
        if (!formData.customerName?.trim()) {
            newErrors.customerName = 'Customer Name is required';
        }
        if (!formData.customerEmail?.trim()) {
            newErrors.customerEmail = 'Customer Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customerEmail)) {
            newErrors.customerEmail = 'Invalid email format';
        }
        if (!formData.expiryDate) {
            newErrors.expiryDate = 'Expiry Date is required';
        }
        if (formData.licenseType === 'Business' && (!formData.userCount || formData.userCount < 1)) {
            newErrors.userCount = 'User count must be at least 1';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, mode]);

    const handleSubmit = useCallback(() => {
        if (!validateForm()) return;
        
        const payload = {
            ...formData,
            expiryDate: formData.expiryDate?.toISOString(),
            companyId: selectedOrganisation?.id,
            allotedByEmail: issuerEmail,
            userCount: formData.licenseType === 'Business' ? formData.userCount : undefined,
        };
        
        onSubmit(payload);
    }, [formData, validateForm, selectedOrganisation, issuerEmail, onSubmit]);

    const handleClose = useCallback(() => {
        setErrors({});
        onClose();
    }, [onClose]);

    const isEditMode = mode === 'edit';

    return (
        <Drawer
            open={open}
            onClose={handleClose}
            anchor="right"
            variant="temporary"
            PaperProps={{
                sx: {
                    width: 480,
                    maxWidth: '100%',
                }
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Header */}
                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    px: 3, 
                    py: 2,
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                bgcolor: alpha(COLORS.primary, 0.08),
                                color: COLORS.primary,
                            }}
                        >
                            {isEditMode ? <EditIcon sx={{ fontSize: 20 }} /> : <VpnKey sx={{ fontSize: 20 }} />}
                        </Avatar>
                        <Typography variant="h6" fontWeight={600}>
                            {isEditMode ? 'Edit License' : 'Issue New License'}
                        </Typography>
                    </Stack>
                    <IconButton onClick={handleClose}>
                        <Close />
                    </IconButton>
                </Box>

                {/* Form */}
                <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
                    <Stack spacing={3}>
                        {/* Info Key - Only in create mode */}
                        {!isEditMode && (
                            <FormControl fullWidth error={!!errors.infoKey}>
                                <FormLabel>Info Key *</FormLabel>
                                <TextField
                                    multiline
                                    rows={3}
                                    placeholder="Paste the Info Key here"
                                    value={formData.infoKey}
                                    onChange={(e) => handleChange('infoKey', e.target.value)}
                                    error={!!errors.infoKey}
                                    disabled={isLoading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ mt: 1, alignSelf: 'flex-start' }}>
                                                <VpnKey fontSize="small" sx={{ color: COLORS.primary }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {errors.infoKey && <FormHelperText>{errors.infoKey}</FormHelperText>}
                            </FormControl>
                        )}

                        {/* License Number - Only in edit mode */}
                        {isEditMode && data && (
                            <FormControl fullWidth>
                                <FormLabel>License Number</FormLabel>
                                <TextField
                                    value={data.licenseNo}
                                    disabled
                                    size="small"
                                />
                            </FormControl>
                        )}

                        {/* System Name - Only in edit mode */}
                        {isEditMode && data?.systemName && (
                            <FormControl fullWidth>
                                <FormLabel>System Name</FormLabel>
                                <TextField
                                    value={data.systemName}
                                    disabled
                                    size="small"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Computer fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </FormControl>
                        )}

                        {isEditMode && <Divider />}

                        {/* Customer Name */}
                        <FormControl fullWidth error={!!errors.customerName}>
                            <FormLabel>Customer Name *</FormLabel>
                            <TextField
                                placeholder="Enter customer name"
                                value={formData.customerName}
                                onChange={(e) => handleChange('customerName', e.target.value)}
                                error={!!errors.customerName}
                                disabled={isLoading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Person fontSize="small" sx={{ color: COLORS.primary }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errors.customerName && <FormHelperText>{errors.customerName}</FormHelperText>}
                        </FormControl>

                        {/* Customer Email */}
                        <FormControl fullWidth error={!!errors.customerEmail}>
                            <FormLabel>Customer Email *</FormLabel>
                            <TextField
                                type="email"
                                placeholder="customer@example.com"
                                value={formData.customerEmail}
                                onChange={(e) => handleChange('customerEmail', e.target.value)}
                                error={!!errors.customerEmail}
                                disabled={isLoading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Email fontSize="small" sx={{ color: COLORS.primary }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {errors.customerEmail && <FormHelperText>{errors.customerEmail}</FormHelperText>}
                        </FormControl>

                        {/* Customer Phone */}
                        <FormControl fullWidth>
                            <FormLabel>Customer Phone</FormLabel>
                            <TextField
                                placeholder="+1 234 567 8900"
                                value={formData.customerPhone}
                                onChange={(e) => handleChange('customerPhone', e.target.value)}
                                disabled={isLoading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Phone fontSize="small" sx={{ color: COLORS.primary }} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </FormControl>

                        {/* License Type */}
                        <FormControl fullWidth>
                            <FormLabel>License Type</FormLabel>
                            <RadioGroup
                                row
                                value={formData.licenseType}
                                onChange={(e) => handleChange('licenseType', e.target.value)}
                                sx={{
                                    '& .MuiFormControlLabel-root': {
                                        mr: 3,
                                    },
                                    '& .MuiRadio-root': {
                                        color: alpha(theme.palette.text.primary, 0.3),
                                    },
                                    '& .Mui-checked': {
                                        color: COLORS.primary,
                                    },
                                }}
                            >
                                <FormControlLabel
                                    value="Individual"
                                    control={<Radio size="small" />}
                                    label="Individual"
                                />
                                <FormControlLabel
                                    value="Business"
                                    control={<Radio size="small" />}
                                    label="Business"
                                />
                            </RadioGroup>
                        </FormControl>

                        {/* Expiry Date */}
                        <FormControl fullWidth error={!!errors.expiryDate}>
                            <FormLabel>Expiry Date *</FormLabel>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={formData.expiryDate}
                                    onChange={(date) => handleChange('expiryDate', date)}
                                    minDate={dayjs().add(1, 'day')}
                                    disabled={isLoading}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            placeholder: "Select expiry date",
                                            error: !!errors.expiryDate,
                                            InputProps: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <CalendarToday fontSize="small" sx={{ color: COLORS.primary }} />
                                                    </InputAdornment>
                                                ),
                                            },
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            {errors.expiryDate && <FormHelperText>{errors.expiryDate}</FormHelperText>}
                        </FormControl>

                        {/* User Count - Business only */}
                        {formData.licenseType === 'Business' && (
                            <FormControl fullWidth error={!!errors.userCount}>
                                <FormLabel>User Count *</FormLabel>
                                <TextField
                                    type="number"
                                    value={formData.userCount}
                                    onChange={(e) => handleChange('userCount', parseInt(e.target.value) || 1)}
                                    disabled={isLoading}
                                    inputProps={{ min: 1 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <People fontSize="small" sx={{ color: COLORS.primary }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                {errors.userCount && <FormHelperText>{errors.userCount}</FormHelperText>}
                            </FormControl>
                        )}

                        {/* Warning for edit mode */}
                        {isEditMode && (
                            <Box
                                sx={{
                                    p: 2,
                                    bgcolor: alpha(COLORS.warning, 0.06),
                                    borderRadius: 2,
                                    border: `1px solid ${alpha(COLORS.warning, 0.2)}`,
                                }}
                            >
                                <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Warning sx={{ fontSize: 14 }} />
                                    New key generated on save
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>

                {/* Footer */}
                <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    px: 3, 
                    py: 2.5,
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                }}>
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleClose}
                        disabled={isLoading}
                        sx={{ borderRadius: 2, textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        sx={{ 
                            borderRadius: 2, 
                            textTransform: 'none', 
                            fontWeight: 600,
                            bgcolor: COLORS.primary,
                            '&:hover': { bgcolor: '#0f9d8b' },
                            '&:disabled': {
                                bgcolor: alpha(COLORS.primary, 0.3),
                            },
                        }}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                            isEditMode ? 'Update License' : 'Issue License'
                        )}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
});

// ----- STATUS PILL -----
const StatusPill = React.memo(({ status, expiryDate }) => {
    const config = getStatusConfig(status, expiryDate);
    const theme = useTheme();

    const colorMap = {
        success: { bg: alpha(COLORS.success, 0.08), text: COLORS.success, border: alpha(COLORS.success, 0.2) },
        error: { bg: alpha(COLORS.error, 0.08), text: COLORS.error, border: alpha(COLORS.error, 0.2) },
        warning: { bg: alpha(COLORS.warning, 0.08), text: COLORS.warning, border: alpha(COLORS.warning, 0.2) },
        default: { bg: alpha(theme.palette.text.secondary, 0.08), text: theme.palette.text.secondary, border: alpha(theme.palette.text.secondary, 0.2) },
    };

    const colors = colorMap[config.color] || colorMap.default;

    return (
        <Chip
            label={config.label}
            size="small"
            icon={config.icon}
            sx={{
                height: 24,
                fontWeight: 600,
                fontSize: '0.7rem',
                bgcolor: colors.bg,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                '& .MuiChip-label': { px: 1.5 },
                '& .MuiChip-icon': { fontSize: 14, ml: 0.5, color: colors.text },
            }}
        />
    );
});

// ----- TABLE COMPONENT -----
const LicenseTable = React.memo(({ list, onAction }) => {
    const theme = useTheme();

    const columns = useMemo(() => [
        {
            id: 'customer',
            label: 'Customer',
            align: 'left',
            flex: 2.2,
            minWidth: 180,
            render: (item) => (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                            color: theme.palette.primary.main,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                        }}
                    >
                        {item.customerName?.charAt(0) || '?'}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" fontWeight={500} noWrap>
                            {item.customerName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" noWrap>
                            {item.customerEmail}
                        </Typography>
                    </Box>
                </Stack>
            ),
        },
        {
            id: 'type',
            label: 'Type',
            align: 'center',
            flex: 0.7,
            minWidth: 85,
            render: (item) => (
                <Chip
                    label={item.licenseType}
                    size="small"
                    variant={item.licenseType === 'Business' ? 'filled' : 'outlined'}
                    color={item.licenseType === 'Business' ? 'primary' : 'default'}
                    sx={{ fontWeight: 500, height: 24, '& .MuiChip-label': { px: 1.5, fontSize: '0.7rem' } }}
                />
            ),
        },
        {
            id: 'machine',
            label: 'Machine',
            align: 'center',
            flex: 1.2,
            minWidth: 120,
            render: (item) => (
                <Box>
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                        <Computer sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight={500} noWrap>
                            {item.systemName || '-'}
                        </Typography>
                    </Stack>
                    {item.systemProfileName && (
                        <Typography variant="caption" color="textSecondary" noWrap>
                            {item.systemProfileName}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            id: 'expiry',
            label: 'Expiry',
            align: 'center',
            flex: 0.9,
            minWidth: 100,
            render: (item) => {
                const expired = isExpired(item.expiryDate);
                return (
                    <Box>
                        <Typography variant="body2" color={expired ? 'error' : 'inherit'} noWrap>
                            {formatDate(item.expiryDate)}
                        </Typography>
                        {expired && (
                            <Typography variant="caption" color="error" display="block" noWrap>
                                Expired
                            </Typography>
                        )}
                    </Box>
                );
            },
        },
        {
            id: 'status',
            label: 'Status',
            align: 'center',
            flex: 0.8,
            minWidth: 95,
            render: (item) => <StatusPill status={item.status} expiryDate={item.expiryDate} />,
        },
        {
            id: 'issuedBy',
            label: 'Issued By',
            align: 'center',
            flex: 1,
            minWidth: 120,
            render: (item) => (
                <Typography variant="body2" color="textSecondary" noWrap>
                    {item.allotedByEmail || '-'}
                </Typography>
            ),
        },
        {
            id: 'actions',
            label: '',
            align: 'center',
            flex: 1.1,
            minWidth: 140,
            render: (item) => {
                const isActive = item.status === 'ACTIVE';
                const isRevoked = item.status === 'REVOKED';
                const expired = isExpired(item.expiryDate);

                return (
                    <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <Tooltip title="Edit" arrow>
                            <IconButton
                                size="small"
                                onClick={() => onAction('edit', item)}
                                sx={{
                                    color: theme.palette.info.main,
                                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.08) },
                                }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        {isActive && !expired && (
                            <Tooltip title="Revoke" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => onAction('revoke', item)}
                                    sx={{
                                        color: theme.palette.warning.main,
                                        '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.08) },
                                    }}
                                >
                                    <Block fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {isRevoked && !expired && (
                            <Tooltip title="Reactivate" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => onAction('reactivate', item)}
                                    sx={{
                                        color: theme.palette.success.main,
                                        '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.08) },
                                    }}
                                >
                                    <RestartAlt fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title="Delete" arrow>
                            <IconButton
                                size="small"
                                onClick={() => onAction('delete', item)}
                                sx={{
                                    color: theme.palette.error.main,
                                    '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.08) },
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                );
            },
        },
    ], [theme, onAction]);

    if (!list || list.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 300,
                    width: '100%',
                }}
            >
                <VpnKey sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.2, mb: 2 }} />
                <Typography variant="h6" color="textSecondary" fontWeight={500}>
                    No licenses found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    Issue a new license
                </Typography>
            </Box>
        );
    }

    const totalFlex = columns.reduce((sum, col) => sum + col.flex, 0);

    return (
        <Box sx={{ flex: 1, width: '100%', overflow: 'hidden' }}>
            <TableVirtuoso
                style={{ 
                    flex: 1, 
                    width: '100%', 
                    overflowX: 'auto',
                    minWidth: '100%',
                }}
                data={list}
                components={TableComponents}
                fixedHeaderContent={() => (
                    <TableHead sx={{ display: 'table-header-group' }}>
                        <TableRow>
                            {columns.map((col) => (
                                <TableCell
                                    key={col.id}
                                    align={col.align}
                                    sx={{
                                        flex: col.flex || 1,
                                        minWidth: col.minWidth,
                                        width: `${(col.flex / totalFlex) * 100}%`,
                                        bgcolor: theme.palette.background.paper,
                                        fontWeight: 700,
                                        fontSize: '0.65rem',
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5,
                                        color: theme.palette.text.secondary,
                                        borderBottom: `2px solid ${alpha(theme.palette.divider, 0.08)}`,
                                        py: 1.5,
                                        whiteSpace: 'nowrap',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 2,
                                    }}
                                >
                                    {col.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                )}
                itemContent={(index, item) => (
                    <TableRow
                        hover
                        sx={{
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                            },
                            transition: 'background-color 0.15s ease',
                            display: 'table-row',
                            width: '100%',
                        }}
                    >
                        {columns.map((col) => (
                            <TableCell
                                key={col.id}
                                align={col.align}
                                sx={{
                                    py: 1.5,
                                    flex: col.flex || 1,
                                    minWidth: col.minWidth,
                                    width: `${(col.flex / totalFlex) * 100}%`,
                                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {col.render(item)}
                            </TableCell>
                        ))}
                    </TableRow>
                )}
            />
        </Box>
    );
});

// ============================================================
// MAIN CONTAINER
// ============================================================
export default function StandaloneLicenseContainer() {
    const theme = useTheme();
    const { handleSnackbar } = useApplicationContext();
    const errorHandler = useErrorHandler();

    // State
    const [loading, setLoading] = useState(false);
    const [isIssuing, setIsIssuing] = useState(false);
    const [organisationList, setOrganisationList] = useState([]);
    const [licenseList, setLicenseList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [selectedOrganisation, setSelectedOrganisation] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [licenseKeyModalOpen, setLicenseKeyModalOpen] = useState(false);
    const [currentLicenseKey, setCurrentLicenseKey] = useState('');
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmData, setConfirmData] = useState(null);
    const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, revoked: 0 });
    const [issueForm, setIssueForm] = useState({
        infoKey: '',
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        licenseType: 'Individual',
        expiryDate: null,
        userCount: 1,
        issuerEmail: '',
    });

    // Drawer states
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState('create'); // 'create' | 'edit'
    const [selectedLicense, setSelectedLicense] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Refs to prevent multiple calls
    const isMounted = useRef(true);
    const loadingRef = useRef(false);
    const isInitialLoadRef = useRef(true);

    // Fetch user email
    const fetchCurrentUser = useCallback(async () => {
        try {
            const response = await getUserDetail_API();
            if (response?.status === HttpStatusCode.Ok && isMounted.current) {
                const email = response.data?.email || response.data?.Email || '';
                setIssueForm(prev => ({ ...prev, issuerEmail: email }));
                return email;
            }
        } catch (error) {
            console.error('Error fetching user:', error);
        }
        return '';
    }, []);

    const getEmailFromStorage = useCallback(() => {
        try {
            const sources = ['user', 'profile'].flatMap(key => [
                localStorage.getItem(key),
                sessionStorage.getItem(key),
            ]);
            for (const source of sources) {
                if (source) {
                    try {
                        const data = JSON.parse(source);
                        if (data?.email) return data.email;
                        if (data?.Email) return data.Email;
                    } catch { /* continue */ }
                }
            }
        } catch { /* ignore */ }
        return '';
    }, []);

    // Load data
    const loadOrganisations = useCallback(async () => {
        if (loadingRef.current) return;
        loadingRef.current = true;
        setLoading(true);
        try {
            const response = await getOrganisationList_API();
            if (response?.status === HttpStatusCode.Ok && isMounted.current) {
                setOrganisationList(response.data || []);
                if (response.data?.length && !selectedOrganisation && isInitialLoadRef.current) {
                    const firstOrg = response.data[0];
                    setSelectedOrganisation(firstOrg);
                    isInitialLoadRef.current = false;
                    await loadLicenses(firstOrg);
                }
            } else {
                errorHandler(response);
            }
        } catch (error) {
            handleSnackbar(String(error), Severity.error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
                loadingRef.current = false;
            }
        }
    }, [errorHandler, handleSnackbar, selectedOrganisation]);

    const loadLicenses = useCallback(async (org) => {
        if (!org?.id) return;
        loadingRef.current = true;
        setLoading(true);
        try {
            const response = await getStandaloneLicenseList_API(`companyId=${org.id}`);
            if (response?.status === HttpStatusCode.Ok && isMounted.current) {
                const licenses = response.data || [];
                setLicenseList(licenses);
            } else {
                errorHandler(response);
            }
        } catch (error) {
            handleSnackbar(String(error), Severity.error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
                loadingRef.current = false;
            }
        }
    }, [errorHandler, handleSnackbar]);

    // Effects
    useEffect(() => {
        isMounted.current = true;
        fetchCurrentUser().then(email => {
            if (!email && isMounted.current) {
                const fallback = getEmailFromStorage();
                if (fallback) {
                    setIssueForm(prev => ({ ...prev, issuerEmail: fallback }));
                }
            }
        });
        loadOrganisations();

        return () => {
            isMounted.current = false;
        };
    }, [fetchCurrentUser, getEmailFromStorage, loadOrganisations]);

    useEffect(() => {
        if (selectedOrganisation && !loadingRef.current) {
            loadLicenses(selectedOrganisation);
        }
    }, [selectedOrganisation, loadLicenses]);

    useEffect(() => {
        const total = licenseList.length;
        const active = licenseList.filter(item => item.status === 'ACTIVE' && !isExpired(item.expiryDate)).length;
        const expired = licenseList.filter(item => isExpired(item.expiryDate)).length;
        const revoked = licenseList.filter(item => item.status === 'REVOKED').length;
        setStats({ total, active, expired, revoked });
    }, [licenseList]);

    useEffect(() => {
        const filter = () => {
            let list = [...licenseList];
            if (statusFilter === 'ACTIVE') {
                list = list.filter(item => item.status === 'ACTIVE');
            } else if (statusFilter === 'REVOKED') {
                list = list.filter(item => item.status === 'REVOKED');
            } else if (statusFilter === 'EXPIRED') {
                list = list.filter(item => isExpired(item.expiryDate));
            }

            if (searchText) {
                const s = searchText.toLowerCase();
                list = list.filter(item =>
                    item.customerName?.toLowerCase().includes(s) ||
                    item.customerEmail?.toLowerCase().includes(s) ||
                    item.systemName?.toLowerCase().includes(s)
                );
            }
            setFilteredList(list);
        };
        const debounced = debounce(filter, 300);
        debounced();
    }, [searchText, statusFilter, licenseList]);

    // Handlers
    const handleOrganisationChange = useCallback((e) => {
        const org = organisationList.find(o => o.id === e.target.value);
        if (org) {
            isInitialLoadRef.current = false;
            setSelectedOrganisation(org);
            loadLicenses(org);
        }
    }, [organisationList, loadLicenses]);

    const handleRefresh = useCallback(() => {
        if (selectedOrganisation && !loadingRef.current) {
            loadLicenses(selectedOrganisation);
        }
    }, [selectedOrganisation, loadLicenses]);

    // Drawer handlers
    const handleOpenCreateDrawer = useCallback(() => {
        setDrawerMode('create');
        setSelectedLicense(null);
        setDrawerOpen(true);
    }, []);

    const handleOpenEditDrawer = useCallback((license) => {
        setDrawerMode('edit');
        setSelectedLicense(license);
        setDrawerOpen(true);
    }, []);

    const handleCloseDrawer = useCallback(() => {
        setDrawerOpen(false);
        setSelectedLicense(null);
        setIsSubmitting(false);
    }, []);

    const handleDrawerSubmit = useCallback(async (payload) => {
        if (isSubmitting) return;
        
        setIsSubmitting(true);
        
        try {
            let response;
            if (drawerMode === 'create') {
                response = await createStandaloneLicense_API(payload);
            } else {
                // Edit mode - update license
                response = await updateStandaloneLicense_API(selectedLicense.licenseNo, payload);
            }

            if (response?.status === HttpStatusCode.Ok || response?.status === HttpStatusCode.Created) {
                const message = drawerMode === 'create' ? 'License issued successfully!' : 'License updated successfully!';
                handleSnackbar(message, Severity.success);
                
                if (response.data?.licenseKey) {
                    setCurrentLicenseKey(response.data.licenseKey);
                    setLicenseKeyModalOpen(true);
                }
                
                handleCloseDrawer();
                await loadLicenses(selectedOrganisation);
            } else {
                errorHandler(response);
            }
        } catch (error) {
            handleSnackbar(String(error), Severity.error);
        } finally {
            setIsSubmitting(false);
        }
    }, [drawerMode, selectedLicense, selectedOrganisation, handleCloseDrawer, loadLicenses, handleSnackbar, errorHandler, isSubmitting]);

    const handleRowAction = useCallback((action, data) => {
        if (action === 'edit') {
            handleOpenEditDrawer(data);
        } else if (action === 'revoke') {
            setConfirmData(data);
            setConfirmAction('revoke');
            setConfirmDialogOpen(true);
        } else if (action === 'reactivate') {
            setConfirmData(data);
            setConfirmAction('reactivate');
            setConfirmDialogOpen(true);
        } else if (action === 'delete') {
            setConfirmData(data);
            setConfirmAction('delete');
            setConfirmDialogOpen(true);
        }
    }, [handleOpenEditDrawer]);

    const handleConfirmAction = useCallback(async () => {
        if (!confirmData || loadingRef.current) return;

        setLoading(true);
        loadingRef.current = true;
        try {
            let response;
            switch (confirmAction) {
                case 'revoke':
                    response = await revokeStandaloneLicense_API(confirmData.licenseNo);
                    break;
                case 'reactivate':
                    response = await reactivateStandaloneLicense_API(confirmData.licenseNo);
                    break;
                case 'delete':
                    response = await deleteStandaloneLicense_API(confirmData.licenseNo);
                    break;
                default:
                    return;
            }

            if (response?.status === HttpStatusCode.Ok && isMounted.current) {
                const messages = {
                    revoke: 'License revoked',
                    reactivate: 'License reactivated',
                    delete: 'License deleted',
                };
                handleSnackbar(messages[confirmAction] || 'Action completed', Severity.success);
                await loadLicenses(selectedOrganisation);
            } else {
                errorHandler(response);
            }
        } catch (error) {
            handleSnackbar(String(error), Severity.error);
        } finally {
            if (isMounted.current) {
                setLoading(false);
                loadingRef.current = false;
                setConfirmDialogOpen(false);
                setConfirmData(null);
            }
        }
    }, [confirmData, confirmAction, selectedOrganisation, loadLicenses, handleSnackbar, errorHandler]);

    const getConfirmContent = useCallback(() => {
        const messages = {
            revoke: {
                title: 'Revoke License',
                description: `Revoke license for ${confirmData?.customerName}?`,
            },
            reactivate: {
                title: 'Reactivate License',
                description: `Reactivate license for ${confirmData?.customerName}?`,
            },
            delete: {
                title: 'Delete License',
                description: `Delete license for ${confirmData?.customerName}? This cannot be undone.`,
            },
        };
        return messages[confirmAction] || { title: '', description: '' };
    }, [confirmAction, confirmData]);

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <Box
            sx={{
                height: '100%',
                overflow: 'hidden',
                bgcolor: 'background.default',
                p: 3,
            }}
        >
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    overflow: 'auto',
                }}
            >
                {/* Toolbar with Issue License Button */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexShrink: 0,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <Select
                                value={selectedOrganisation?.id || ''}
                                onChange={handleOrganisationChange}
                                displayEmpty
                                disabled={!organisationList.length || loading}
                                startAdornment={<InputAdornment position="start"><Business fontSize="small" /></InputAdornment>}
                            >
                                <MenuItem value="" disabled>Select Organisation</MenuItem>
                                {organisationList.map((org) => (
                                    <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Tooltip title="Refresh">
                            <IconButton
                                size="small"
                                onClick={handleRefresh}
                                disabled={!selectedOrganisation || loading}
                                sx={{
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    borderRadius: 2,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                                        borderColor: theme.palette.primary.main,
                                    },
                                }}
                            >
                                <Refresh fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenCreateDrawer}
                        disabled={!selectedOrganisation || loading}
                        sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: COLORS.primary,
                            '&:hover': { bgcolor: '#0f9d8b' },
                            '&:disabled': {
                                bgcolor: alpha(COLORS.primary, 0.3),
                            },
                        }}
                    >
                        Issue License
                    </Button>
                </Box>

                {/* KPI Cards */}
                <KPICards stats={stats} />

                {/* Table */}
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                        overflow: 'hidden',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 300,
                    }}
                >
                    {/* Table Toolbar */}
                    <Box
                        sx={{
                            px: 3,
                            py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
                            flexShrink: 0,
                            flexWrap: 'wrap',
                            gap: 1,
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" gap={1}>
                            <TextField
                                placeholder="Search..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                size="small"
                                sx={{ width: 280 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchOutlined fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <FormControl size="small" sx={{ width: 140 }}>
                                <Select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    displayEmpty
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="REVOKED">Revoked</MenuItem>
                                    <MenuItem value="EXPIRED">Expired</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>
                        <Typography variant="caption" color="textSecondary">
                            {filteredList.length} license{filteredList.length !== 1 ? 's' : ''}
                        </Typography>
                    </Box>

                    {/* Table */}
                    <LicenseTable list={filteredList} onAction={handleRowAction} />
                </Paper>
            </Box>

            {/* License Drawer */}
            <LicenseDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                mode={drawerMode}
                data={selectedLicense}
                onSubmit={handleDrawerSubmit}
                selectedOrganisation={selectedOrganisation}
                isLoading={isSubmitting}
                issuerEmail={issueForm.issuerEmail}
            />

            {/* License Key Modal */}
            <LicenseKeyModal
                open={licenseKeyModalOpen}
                licenseKey={currentLicenseKey}
                onClose={() => {
                    setLicenseKeyModalOpen(false);
                    setCurrentLicenseKey('');
                }}
            />

            {/* Confirmation Dialog */}
            {confirmDialogOpen && (
                <ConfirmationDialog
                    open={confirmDialogOpen}
                    title={getConfirmContent().title}
                    description={getConfirmContent().description}
                    handleClose={() => {
                        setConfirmDialogOpen(false);
                        setConfirmData(null);
                    }}
                    handleContinue={handleConfirmAction}
                />
            )}
        </Box>
    );
}